import type { Product } from '../types';

import { bytesToGigabytes } from './bytes-to-gigabytes';

type StorageData =
	| {
			attachmentStorageAllowed?: number | null;
			attachmentStorageIsUnlimited?: boolean | null;
			attachmentStorageUsed?: number | null;
			product: Product;
	  }
	| null
	| undefined;

export type MostCriticalStorage = {
	product?: Product;
	storageIsUnlimited?: boolean;
	storageLimitInGB?: number;
	storageUsageInGB?: number;
};

/**
 * Given an array of storage data sources (e.g. JSM and CSM), returns the storage
 * figures for whichever product is closest to reaching its limit (highest
 * used/allowed ratio). Unlimited products are excluded from comparison so
 * the limited product is always preferred.
 *
 * If all are unlimited, returns { storageIsUnlimited: true }.
 * If no data is provided, returns {}.
 */
export function getMostCriticalStorage(storageDataArray: StorageData[]): MostCriticalStorage {
	const allUnlimited =
		storageDataArray.length > 0 &&
		storageDataArray.every((data) => data?.attachmentStorageIsUnlimited ?? false);

	if (allUnlimited) {
		return { storageIsUnlimited: true };
	}

	let winner: StorageData = null;
	let winnerRatio = -Infinity;

	for (const data of storageDataArray) {
		const isUnlimited = data?.attachmentStorageIsUnlimited ?? false;
		if (isUnlimited) {
			// Unlimited products are never preferred over limited ones
			continue;
		}
		const used = data?.attachmentStorageUsed ?? 0;
		const allowed = data?.attachmentStorageAllowed ?? Infinity;
		const ratio = allowed > 0 ? used / allowed : used > 0 ? Infinity : 0;
		if (ratio > winnerRatio) {
			winnerRatio = ratio;
			winner = data;
		}
	}

	if (!winner) {
		return {};
	}

	return {
		product: winner.product,
		storageUsageInGB:
			typeof winner.attachmentStorageUsed === 'number'
				? bytesToGigabytes(winner.attachmentStorageUsed)
				: undefined,
		storageLimitInGB:
			typeof winner.attachmentStorageAllowed === 'number'
				? bytesToGigabytes(winner.attachmentStorageAllowed)
				: undefined,
		storageIsUnlimited: winner.attachmentStorageIsUnlimited ?? undefined,
	};
}
